import type { ComponentType, CSSProperties, SVGProps } from "react";
import type { MyCommunity, RailFolder, RailFolderIcon } from "@atrium/shared";
import { cn } from "../../lib/cn";
import {
  IconBolt,
  IconBook,
  IconCase,
  IconChatLine,
  IconCode,
  IconFire,
  IconFolder,
  IconGamepad,
  IconGlobe,
  IconHeart,
  IconLeaf,
  IconMoon,
  IconMusicNote,
  IconPalette,
  IconStar,
  IconSun,
} from "../../components/icons";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

export const FOLDER_ICONS: Record<RailFolderIcon, { icon: Icon; label: string }> = {
  folder: { icon: IconFolder, label: "Folder" },
  star: { icon: IconStar, label: "Star" },
  heart: { icon: IconHeart, label: "Heart" },
  gamepad: { icon: IconGamepad, label: "Games" },
  music: { icon: IconMusicNote, label: "Music" },
  code: { icon: IconCode, label: "Code" },
  book: { icon: IconBook, label: "Book" },
  briefcase: { icon: IconCase, label: "Work" },
  fire: { icon: IconFire, label: "Fire" },
  bolt: { icon: IconBolt, label: "Bolt" },
  leaf: { icon: IconLeaf, label: "Leaf" },
  moon: { icon: IconMoon, label: "Moon" },
  sun: { icon: IconSun, label: "Sun" },
  globe: { icon: IconGlobe, label: "Globe" },
  chat: { icon: IconChatLine, label: "Chat" },
  palette: { icon: IconPalette, label: "Palette" },
};

/** Folder colours are user-chosen data; with none, the folder follows the accent token. */
export function folderTint(color: string | null): { tile: CSSProperties; fg: CSSProperties } {
  return color
    ? { tile: { background: `color-mix(in srgb, ${color} 22%, transparent)` }, fg: { color } }
    : { tile: { background: "var(--accent-soft)" }, fg: { color: "var(--accent)" } };
}

/** The folder's icon or emoji on its own (no tile). */
export function FolderSymbol({ icon, className, style }: { icon: string | null; className?: string; style?: CSSProperties }) {
  const def = icon && icon in FOLDER_ICONS ? FOLDER_ICONS[icon as RailFolderIcon] : null;
  if (icon && !def)
    return (
      <span aria-hidden className={cn("leading-none", className)} style={style}>
        {icon}
      </span>
    );
  const I = def?.icon ?? IconFolder;
  return <I aria-hidden className={cn("icon", className)} style={style} />;
}

function Mini({ c }: { c: MyCommunity }) {
  return c.iconUrl ? (
    <img src={c.iconUrl} alt="" className="size-4 rounded-[4px] bg-surface-2 object-cover" />
  ) : (
    <span className="flex size-4 items-center justify-center rounded-[4px] bg-surface-2 text-[9px] font-semibold leading-none text-fg shadow-sm">{c.name.slice(0, 1).toUpperCase()}</span>
  );
}

/** Collapsed folder: its icon, or a 2×2 grid of its first communities, on a tile tinted with its colour. */
export function FolderTile({ folder, communities, className }: { folder: Pick<RailFolder, "color" | "icon">; communities: MyCommunity[]; className?: string }) {
  const tint = folderTint(folder.color);
  return (
    <span aria-hidden className={cn("flex size-10 items-center justify-center rounded-[11px]", className)} style={tint.tile}>
      {folder.icon ? (
        <FolderSymbol icon={folder.icon} className={folder.icon in FOLDER_ICONS ? "size-[22px]" : "text-xl"} style={tint.fg} />
      ) : (
        <span className="grid grid-cols-2 gap-0.5">
          {communities.slice(0, 4).map((c) => (
            <Mini key={c.id} c={c} />
          ))}
        </span>
      )}
    </span>
  );
}
